import {
  iconNavAdicionar1,
  iconNavMenuHamburger,
  iconNavProdutos,
} from "@bsds-lib-component/icons/icons";
import { BsdsInput } from "@bsds-lib-component/react";
import React, { useMemo, useState } from "react";

import { Button, Visibility } from "../../components";
import Dropdown from "../../components/dropdown/Dropdown";
import Pagination from "../../components/pagination/Pagination";
import { useCompanyStore } from "../../store/companyStore";
import type { UserPoliciesListItem } from "../../types/serviceType";
import SubEstipulantesTable from "../../components/table/vida/SubEstipulantesTable";
import { useHomeContent } from "../../hooks";
import {
  SUBESTIPULANTES_SORT_OPTIONS,
  formatDocSubestipulante,
  formatVigenciaSubDisplay,
} from "../../hooks/useHomeContent";
import HomeApoliceDataCard from "../../components/pagesComponents/homeContentComponents/HomeApoliceDataCard";
import HomeSubestipulanteCard from "../../components/pagesComponents/homeContentComponents/HomeSubestipulanteCard";

const HomeContentPage: React.FC = () => {
  const {
    isAdmin,
    subestipulantes,
    mainContentId,
    actionsRegionId,
    subestipulantesHeadingId,
    handleNavigateToPoliceholder,
    subestipulantesPanelId,
    subEstSearchInputId,
    subestipulantesExpanded,
    toggleSubestipulantesExpanded,
    subestipulantesView,
    toggleSubestipulantesView,
    subEstSearchTerm,
    setSubEstSearchTerm,
    subEstSortBy,
    setSubEstSortBy,
    subestipulantesPaginated,
    subestipulantesTotalPages,
    subestipulantesPage,
    setSubestipulantesPage,
    subestipulantesFilteredSorted,
  } = useHomeContent();

  const selectedCompany = useCompanyStore((state) => state.selectedCompany);
  const [subestipulantesCardFilter, setSubestipulantesCardFilter] = useState<
    "apolice" | "subestipulantes"
  >("apolice");

  const selectedPolicyRow = useMemo<UserPoliciesListItem | null>(() => {
    const selectedPolicyNumber = Number(
      String(selectedCompany?.policySelection?.numeroApolice ?? "").replace(
        /\D/g,
        "",
      ),
    );
    const rows =
      selectedCompany?.policySelection?.policiesListResponse
        ?.flatMap((group) => group.retorno ?? [])
        .filter((row): row is UserPoliciesListItem => row != null) ?? [];

    if (rows.length === 0) {
      return null;
    }

    if (Number.isFinite(selectedPolicyNumber) && selectedPolicyNumber > 0) {
      const matched = rows.find((row) => {
        const numApolice = Number(
          String(row.numApoliceApol ?? "").replace(/\D/g, ""),
        );
        return (
          Number.isFinite(numApolice) && numApolice === selectedPolicyNumber
        );
      });
      if (matched) {
        return matched;
      }
    }

    return rows[0];
  }, [selectedCompany]);

  const policyCardData = useMemo(() => {
    const policySelection = selectedCompany?.policySelection;
    const apoliceContext = selectedCompany?.apoliceContext;

    const numeroApolice = String(
      selectedPolicyRow?.numApoliceApol ??
        policySelection?.numeroApolice ??
        apoliceContext?.numApoliceApol ??
        "—",
    ).trim();

    const ramoNome = String(
      selectedPolicyRow?.nomeRamoApol ?? policySelection?.ramoNome ?? "",
    ).trim();
    const ramoCodigo = String(
      selectedPolicyRow?.codRamoApol ?? policySelection?.ramoCodigo ?? "",
    ).trim();
    const ramo =
      ramoNome && ramoCodigo
        ? `${ramoNome} (${ramoCodigo})`
        : ramoNome || ramoCodigo || "—";

    const contrato = String(
      selectedPolicyRow?.identContrato ??
        selectedPolicyRow?.identContratoApol ??
        apoliceContext?.identContrato ??
        apoliceContext?.identContratoApol ??
        "—",
    ).trim();

    return {
      numeroApolice: numeroApolice || "—",
      empresa: String(selectedPolicyRow?.nomeEmpresaApol ?? "").trim() || "—",
      estipulante:
        String(selectedPolicyRow?.nomeEstipulanteApol ?? "").trim() || "—",
      sucursal:
        String(
          selectedPolicyRow?.nomeSucursalNegocio ??
            selectedPolicyRow?.nomeSurcusalApol ??
            "",
        ).trim() || "—",
      produto: String(selectedPolicyRow?.nomeProdutoApol ?? "").trim() || "—",
      ramo,
      contrato: contrato || "—",
      identEstipulante:
        String(
          selectedPolicyRow?.identEstipulanteApol ??
            apoliceContext?.identEstipulanteApol ??
            "—",
        ).trim() || "—",
      codEmpresa:
        String(
          selectedPolicyRow?.codEmpresaApol ??
            apoliceContext?.codEmpresaApol ??
            "—",
        ).trim() || "—",
      codSucursal:
        String(
          selectedPolicyRow?.codSucursalApol ??
            apoliceContext?.codSucursalApol ??
            "—",
        ).trim() || "—",
      codProduto:
        String(
          selectedPolicyRow?.codprodutoApol ??
            apoliceContext?.codprodutoApol ??
            "—",
        ).trim() || "—",
      codRamo:
        String(
          selectedPolicyRow?.codRamoApol ?? apoliceContext?.codRamoApol ?? "—",
        ).trim() || "—",
      documento: formatDocSubestipulante(
        selectedPolicyRow?.numDocumentoApol ??
          policySelection?.cnpj ??
          apoliceContext?.numDocumentoApol,
      ),
      vigenciaInicio: formatVigenciaSubDisplay(
        selectedPolicyRow?.dataInicioVigencia,
      ),
      vigenciaFim: formatVigenciaSubDisplay(selectedPolicyRow?.dataFimVigencia),
    };
  }, [selectedCompany, selectedPolicyRow]);

  return (
    <div
      id={mainContentId}
      className="bsds-width-100"
      role="main"
      aria-label="Conteúdo da página inicial"
    >
      <div className="bsds-display-flex bsds-align-items-center bsds-justify-content-between bsds-width-100 bsds-mb-16">
        <div className="bsds-display-flex bsds-align-items-center bsds-justify-content-start">
          <div
            className="w-[2.5px] h-8 bg-red-600 bsds-mr-12"
            aria-hidden="true"
          />
          <h2 className="opacity-80">Início</h2>
        </div>
        <div className="bsds-display-flex bsds-justify-content-end">
          <Visibility isActive={isAdmin} animated animationDuration={200}>
            <div
              id={actionsRegionId}
              role="region"
              aria-label="Ações disponíveis"
            >
              <Button
                onClick={handleNavigateToPoliceholder}
                type="button"
                icon={iconNavAdicionar1}
                className="bsds-width-100"
                iconPosition="start"
                aria-label="Cadastrar Representante"
              >
                Cadastrar Representante
              </Button>
            </div>
          </Visibility>
        </div>
      </div>

      {subestipulantes.length > 0 && (
        <section
          className="bsds-mb-24"
          aria-labelledby={subestipulantesHeadingId}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bsds-mb-12">
            {subestipulantesExpanded && (
              <div
                id={subestipulantesHeadingId}
                className="grid grid-cols-2 gap-2 w-full sm:w-auto"
              >
                <Button
                  type="button"
                  fill={
                    subestipulantesCardFilter === "apolice"
                      ? "solid"
                      : "outline"
                  }
                  color="primary"
                  onClick={() => {
                    setSubestipulantesCardFilter("apolice");
                    if (subestipulantesView === "table") {
                      toggleSubestipulantesView();
                    }
                  }}
                  aria-label="Mostrar card da apólice"
                  className="w-full text-sm"
                >
                  Apólice
                </Button>
                <Button
                  type="button"
                  fill={
                    subestipulantesCardFilter === "subestipulantes"
                      ? "solid"
                      : "outline"
                  }
                  color="primary"
                  onClick={() =>
                    setSubestipulantesCardFilter("subestipulantes")
                  }
                  aria-label="Mostrar cards de subestipulantes"
                  className="w-full text-sm"
                >
                  Sub-Estipulantes
                </Button>
              </div>
            )}
            <Button
              type="button"
              fill="outline"
              color="primary"
              onClick={toggleSubestipulantesExpanded}
              aria-expanded={subestipulantesExpanded}
              aria-controls={subestipulantesPanelId}
              className="ml-auto self-end"
              aria-label={
                subestipulantesExpanded
                  ? "Ocultar lista de subestipulantes"
                  : "Expandir e mostrar subestipulantes"
              }
            >
              {subestipulantesExpanded ? "Ocultar" : ""} Dados da Apólice
            </Button>
          </div>

          <Visibility
            isActive={subestipulantesExpanded}
            animated
            animationDuration={300}
          >
            <div
              id={subestipulantesPanelId}
              role="region"
              aria-label="Painel de subestipulantes"
              className="bsds-width-100"
            >
              {subestipulantesCardFilter !== "apolice" && (
                <div className="bsds-mb-16 bsds-mt-0">
                  <div
                    className="flex flex-col sm:flex-row gap-4 w-full"
                    role="group"
                    aria-label="Ferramentas de busca e ordenação dos subestipulantes"
                  >
                    <div className="flex-1">
                      <BsdsInput
                        id={subEstSearchInputId}
                        type="text"
                        placeholder="Filtre por nome, documento ou status"
                        value={subEstSearchTerm}
                        onInput={(e) => {
                          const target = e.target as HTMLBsdsInputElement;
                          const value =
                            (
                              e as React.FormEvent<HTMLBsdsInputElement> & {
                                detail?: { value: string };
                              }
                            ).detail?.value ||
                            target?.value ||
                            "";
                          setSubEstSearchTerm(String(value));
                        }}
                        platform="web"
                        aria-label="Campo de busca nos subestipulantes"
                        aria-describedby={`${subEstSearchInputId}-help`}
                      />
                      <span
                        id={`${subEstSearchInputId}-help`}
                        className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
                        style={{
                          clip: "rect(0, 0, 0, 0)",
                          clipPath: "inset(50%)",
                        }}
                      >
                        Digite para filtrar por nome do subestipulante, número
                        ou formato do documento, ou status
                      </span>
                    </div>

                    <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-end sm:gap-3">
                      <div className="w-full min-w-0 sm:w-60">
                        <Dropdown
                          id="sort-subestipulantes"
                          placeholder="Ordenar por..."
                          value={subEstSortBy}
                          onChange={setSubEstSortBy}
                          options={SUBESTIPULANTES_SORT_OPTIONS}
                        />
                      </div>
                      <Button
                        type="button"
                        fill="outline"
                        color="primary"
                        icon={
                          subestipulantesView === "cards"
                            ? iconNavMenuHamburger
                            : iconNavProdutos
                        }
                        iconPosition="start"
                        onClick={toggleSubestipulantesView}
                        aria-pressed={subestipulantesView === "table"}
                        aria-label={
                          subestipulantesView === "cards"
                            ? "Alternar para visualização em tabela"
                            : "Alternar para visualização em cards"
                        }
                        className="w-full shrink-0 sm:w-auto sm:min-w-10 bsds-mb-4"
                      >
                        {null}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <Visibility
                isActive={subestipulantesView === "cards"}
                animated
                animationDuration={200}
              >
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                  role="list"
                  aria-label="Subestipulantes da apólice selecionada em cards"
                >
                  {subestipulantesCardFilter === "apolice" && (
                    <HomeApoliceDataCard
                      numeroApolice={policyCardData.numeroApolice}
                      empresa={policyCardData.empresa}
                      estipulante={policyCardData.estipulante}
                      sucursal={policyCardData.sucursal}
                      produto={policyCardData.produto}
                      ramo={policyCardData.ramo}
                      contrato={policyCardData.contrato}
                      identEstipulante={policyCardData.identEstipulante}
                      codEmpresa={policyCardData.codEmpresa}
                      codSucursal={policyCardData.codSucursal}
                      codProduto={policyCardData.codProduto}
                      codRamo={policyCardData.codRamo}
                      documento={policyCardData.documento}
                      vigenciaInicio={policyCardData.vigenciaInicio}
                      vigenciaFim={policyCardData.vigenciaFim}
                    />
                  )}
                  {subestipulantesCardFilter === "subestipulantes" &&
                    subestipulantesPaginated.map((s) => (
                      <HomeSubestipulanteCard
                        key={`${s.identSubestipulanteOut}-${s.codSubestipulanteOut}`}
                        data={s}
                        formatDocumento={formatDocSubestipulante}
                        formatVigencia={formatVigenciaSubDisplay}
                      />
                    ))}
                </div>
              </Visibility>

              <Visibility
                isActive={subestipulantesView === "table"}
                animated
                animationDuration={200}
              >
                <SubEstipulantesTable
                  items={subestipulantesPaginated}
                  formatVigencia={formatVigenciaSubDisplay}
                  formatDocumento={formatDocSubestipulante}
                />
              </Visibility>

              {!(
                subestipulantesCardFilter === "apolice" &&
                subestipulantesView === "cards"
              ) && (
                <div className="bsds-mt-16 bsds-mb-8">
                  <Pagination
                    currentPage={Math.min(
                      subestipulantesPage,
                      subestipulantesTotalPages,
                    )}
                    totalPages={subestipulantesTotalPages}
                    onPageChange={setSubestipulantesPage}
                    totalItems={subestipulantesFilteredSorted.length}
                    itemLabel="Sub-Estipulantes"
                  />
                </div>
              )}
            </div>
          </Visibility>
        </section>
      )}
    </div>
  );
};

export default HomeContentPage;
